/**
 * Auth API — typed handlers for Q076 endpoints.
 * Source of truth: api/openapi.yaml.
 * Phase 6 mid: replace with `openapi-typescript` generated types.
 */
import { API } from './client'

export interface UserProfile {
  id: string
  email: string
  email_verified: boolean
  display_name?: string | null
  avatar_url?: string | null
  created_at: string
  // US-E2-001 — NULL пока юзер не нажал «Начать» на /welcome.
  welcomed_at?: string | null
}

export interface TokenResponse {
  access_token: string
  token_type: 'Bearer'
  expires_in: number
  user: UserProfile
}

export interface RegisterRequest {
  email: string
  password: string
  consent_pdn: boolean
}

export interface RegisterResponse {
  user_id: string
  email: string
  verification_required: boolean
}

export interface LoginRequest {
  email: string
  password: string
  remember?: boolean
}

export const authApi = {
  register: (body: RegisterRequest) =>
    API.post<RegisterResponse>('/api/v1/auth/register', body),

  login: (body: LoginRequest) =>
    API.post<TokenResponse>('/api/v1/auth/token', body),

  recover: (email: string) =>
    API.post<void>('/api/v1/auth/recover', { email }),

  // US-E1-003 — set new password by reset token (B-2 protocol №031)
  reset: (body: { token: string; new_password: string }) =>
    API.post<void>('/api/v1/auth/reset', body),

  me: () => API.get<UserProfile>('/api/v1/auth/me'),

  // US-E2-001 — отметить welcome завершённым; возвращает обновлённый profile.
  welcomeComplete: () => API.post<UserProfile>('/api/v1/auth/welcome-complete', {}),

  oauthStart: async (provider: 'google' | 'yandex') => {
    const res = await API.get<{ authorize_url: string; state: string }>(
      `/api/v1/auth/oauth/${provider}/start`
    )
    return res
  },

  verifyEmail: (token: string) =>
    API.get<{ ok: boolean; email?: string }>(
      `/api/v1/auth/verify?token=${encodeURIComponent(token)}`
    ),

  // Q119-Q121 protocol №030 — resend verification email.
  // Always 200 with generic message (anti-enumeration). 429 = per-IP cap exceeded.
  resendVerification: (email: string) =>
    API.post<{ message: string }>('/api/v1/auth/resend-verification', { email }),

  // OAuth callback exchange — провайдер редиректит сюда, фронт пересылает code
  // на бэк, бэк выдаёт session-cookies. Backend endpoint TBD (Phase 6 mid).
  oauthCallback: (body: { provider: 'google' | 'yandex'; code: string; state: string }) =>
    API.post<TokenResponse>('/api/v1/auth/oauth/callback', body),

  // ========== PIN (US-E1-003 part 3) ==========
  pinSetup: (body: { pin: string; device_id: string; device_label?: string }) =>
    API.post<{ pin_id: string; device_id: string; device_label?: string }>(
      '/api/v1/auth/pin/setup',
      body
    ),

  pinVerify: (body: { email: string; pin: string; device_id: string }) =>
    API.post<TokenResponse>('/api/v1/auth/pin/verify', body),

  pinDelete: (device_id: string) =>
    API.delete<void>('/api/v1/auth/pin', { device_id }),

  pinDevices: () =>
    API.get<{
      devices: Array<{
        device_id: string
        device_label?: string | null
        last_used_at?: string | null
        created_at: string
      }>
    }>('/api/v1/auth/pin/devices'),
}

// ========== US-E2-002 Onboarding survey ==========
export interface OnboardingSurvey {
  answers: Record<string, unknown>
  goal_24h?: string | null
  goal_30d?: string | null
  completed_at?: string | null
}

export const onboardingApi = {
  getSurvey: () => API.get<OnboardingSurvey>('/api/v1/onboarding/survey'),
  patchSurvey: (body: Partial<OnboardingSurvey>) =>
    API.patch<OnboardingSurvey>('/api/v1/onboarding/survey', body),
  completeSurvey: () =>
    API.post<OnboardingSurvey>('/api/v1/onboarding/survey/complete', {}),
}
